import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '../../components/SEOHead';

export default function TermsPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="Terms & Conditions | Technelle | Waukesha, WI"
        description="Read Technelle's terms and conditions governing the use of our website and AI marketing consulting services in Waukesha, Wisconsin."
        keywords="Technelle terms of service, terms and conditions, marketing agency Wisconsin"
        canonical="https://technelle.ai/terms"
        ogTitle="Terms & Conditions | Technelle"
        ogDescription="Terms and conditions governing use of Technelle's website and services."
        ogUrl="https://technelle.ai/terms"
        robots="index, follow"
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'Terms & Conditions', url: 'https://technelle.ai/terms' },
        ]}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82"
              alt="Technelle - AI Marketing & Consulting"
              className="h-14"
            />
          </Link>
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Home</Link>
            <Link to="/about" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Services</Link>
            <Link to="/industries" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Industries</Link>
            <Link to="/resources" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Resources</Link>
          </div>
          <button
            onClick={() => setMenuOpen(true)}
            className="bg-[#2196F3] text-white px-6 sm:px-8 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer min-w-[44px] min-h-[44px]"
            aria-label="Book free strategy call"
          >
            Book Free Strategy Call
          </button>
        </div>
      </nav>

      {/* Contact Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-end">
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
            <div className="p-8 sm:p-12">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-[28px] sm:text-[32px] font-bold text-[#1a1a1a]">Let's talk —</h2>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="text-[#1a1a1a] hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close"
                >
                  <span className="text-[15px] font-medium">Close</span>
                </button>
              </div>
              <p className="text-[15px] text-gray-600">
                Reach out at{' '}
                <a href="tel:4145309114" className="text-[#2196F3] hover:underline">414-530-9114</a>
                {' '}or{' '}
                <a href="mailto:melissa@technelle.ai" className="text-[#2196F3] hover:underline">melissa@technelle.ai</a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="pt-36 pb-16 bg-gray-50">
        <div className="container mx-auto px-8 max-w-4xl">
          <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Legal</div>
          <h1 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-4 leading-tight">Terms &amp; Conditions</h1>
          <p className="text-[16px] text-gray-500">Last updated: April 2, 2026</p>
        </div>
      </section>

      {/* Content */}
      <main className="py-16">
        <div className="container mx-auto px-8 max-w-4xl">
          <div className="prose prose-lg max-w-none space-y-10 text-[16px] text-gray-700 leading-relaxed">

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">1. Agreement to Terms</h2>
              <p>
                By accessing or using the website at <strong>technelle.ai</strong> or engaging with any services provided by Technelle ("Company," "we," "us," or "our"), you agree to be bound by these Terms &amp; Conditions. If you do not agree, please do not use our website or services.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">2. Services</h2>
              <p>
                Technelle provides AI marketing strategy, consulting, workflow automation, SEO, paid social advertising, content strategy, and fractional CMO services to businesses, primarily in Wisconsin. The specific scope, deliverables, and terms of any engagement are governed by a separate written agreement or statement of work between Technelle and the client.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">3. Intellectual Property</h2>
              <p>
                All content on this website — including text, graphics, logos, images, and software — is the property of Technelle or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
              <p className="mt-3">
                Work product created specifically for a client under a paid engagement is governed by the terms of that engagement agreement.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">4. User Conduct</h2>
              <p>When using our website, you agree not to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Use the site for any unlawful purpose or in violation of any regulations</li>
                <li>Attempt to gain unauthorized access to any part of the site or its related systems</li>
                <li>Transmit any harmful, offensive, or disruptive content</li>
                <li>Scrape, crawl, or harvest data from the site without our written consent</li>
                <li>Impersonate Technelle or any of its employees or representatives</li>
              </ul>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">5. Disclaimer of Warranties</h2>
              <p>
                Our website and its content are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied. We do not warrant that the site will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
              <p className="mt-3">
                Results described on this website (such as traffic increases or lead growth) reflect specific client outcomes and are not guarantees of future performance. Individual results will vary based on many factors.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">6. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, Technelle shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our website or services, even if we have been advised of the possibility of such damages.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">7. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. These links are provided for your convenience only. We have no control over the content of those sites and accept no responsibility for them or for any loss or damage that may arise from your use of them.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">8. Privacy</h2>
              <p>
                Your use of our website is also governed by our{' '}
                <Link to="/privacy-policy" className="text-[#2196F3] hover:underline">Privacy Policy</Link>,
                which is incorporated into these Terms by reference.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">9. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Wisconsin, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Waukesha County, Wisconsin.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">10. Changes to These Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of the site after any changes constitutes your acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">11. Contact Us</h2>
              <p>If you have questions about these Terms, please contact us:</p>
              <div className="mt-4 bg-gray-50 rounded-xl p-6 space-y-2">
                <p><strong>Technelle</strong></p>
                <p>Waukesha, Wisconsin</p>
                <p>
                  Email:{' '}
                  <a href="mailto:melissa@technelle.ai" className="text-[#2196F3] hover:underline">melissa@technelle.ai</a>
                </p>
                <p>
                  Phone:{' '}
                  <a href="tel:4145309114" className="text-[#2196F3] hover:underline">414-530-9114</a>
                </p>
              </div>
            </section>

          </div>

          <div className="mt-16 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to="/privacy-policy" className="text-[#2196F3] hover:underline text-[15px] font-medium">
              View Privacy Policy
            </Link>
            <span className="hidden sm:block text-gray-300">|</span>
            <Link to="/" className="text-gray-600 hover:text-[#2196F3] transition-colors text-[15px]">
              &larr; Back to Home
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-12 mt-16">
        <div className="container mx-auto px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-[14px] text-gray-400">Technelle™ &nbsp;·&nbsp; Waukesha, Wisconsin</div>
            <div className="flex items-center gap-6 text-[14px]">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-[#2196F3] font-medium">Terms</Link>
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
